import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { atom } from 'nanostores'
import { useMouseParallax, useScrollParallax } from './modules/_fn.js'

window.process = { env: { NODE_ENV: 'production' } }

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)


const useScrollPagination = () => {
	const sections = [...document.querySelectorAll('[data-pagination-target]')]
	if (!sections.length) return

	const bullets = [...document.querySelectorAll('.home-pagination__bullet')]
	const scrollBtn = document.querySelector('.scroll__button')
	const guitarShadow = document.querySelector('.banner__guitar__shadow')

	const $activeIndex = atom(0)

	sections.forEach((section, index) => {
		const start = index === '0' ? 'top top' : 'top top'
		const end = 'bottom bottom'

		ScrollTrigger.create({
			trigger: section,
			start,
			end,
			// markers: true,
			onEnter: () => {
				$activeIndex.set(index)
			},
			onEnterBack: () => {
				$activeIndex.set(index)
			}
		})
	})

	bullets.forEach((bullet, index) => {
		const offsetY = index === 0 ? 0 : -10

		bullet.addEventListener('click', () => {
			gsap.to(window, {
				duration: 1,
				ease: 'power4.inOut',
				scrollTo: {
					y: sections[index],
					offsetY,
				},
			})
		})
	});

	[scrollBtn, guitarShadow].forEach((el) => {
		el.addEventListener('click', () => {
			gsap.to(window, {
				duration: 1,
				ease: 'power4.inOut',
				scrollTo: {
					y: sections[1],
					offsetY: -10,
				},
			})
		})
	})

	$activeIndex.subscribe((index) => {
		bullets.forEach((bullet, i) => {
			bullet.classList.toggle('is-active', i === index)
		})
	})
}

const useGsapTimeline = () => {
	const guitarCenter = document.querySelector('.banner__guitar__center')
	const guitarShadow = document.querySelector('.banner__guitar__shadow')
	const header = document.querySelector('.header')
	const headerLogo = header?.querySelector('.header__logo')
	const snsGroup = document.querySelector('.home-sns__group')

	const getScale = () => {
		const containerSize = Math.sqrt(
			Math.pow(window.outerWidth, 2) + Math.pow(window.outerHeight, 2)
		)
		const guitarCenterSize = (guitarCenter?.clientWidth || 0) * 0.82
		return containerSize / guitarCenterSize
	}

	const getLogoScaleRatio = () => {
		const headerLogoSize = headerLogo?.clientWidth || 0
		const guitarCenterSize = (guitarCenter?.clientWidth || 0) * 0.82 * 0.81
		return Number((guitarCenterSize / headerLogoSize).toFixed(2))
	}

	let bannerTl
	let productsTl
	const useTimeline = () => {
		bannerTl && bannerTl.kill()
		productsTl && productsTl.kill()

		bannerTl = gsap
			.timeline({
				scrollTrigger: {
					trigger: '.banner',
					start: 'top top',
					end: 'bottom bottom',
					scrub: true,
					onLeave: () => {
						header.classList.add('is-white')
						snsGroup.classList.add('is-white')
						guitarShadow.classList.add('pointer-events-none')

					},
					onEnterBack: () => {
						header.classList.remove('is-white')
						snsGroup.classList.remove('is-white')
						guitarShadow.classList.remove('pointer-events-none')
					}
				}
			})
			.to(
				'.banner__bg__group',
				{
					scale: () => getScale(),
					duration: 2,
				}
			)
			.to(
				'.banner__bg__group',
				{
					'--blur-strength': 32,
					duration: 1,
				},
				'-=1.5'
			)
			.to(
				'.banner__bg__group',
				{
					opacity: 0,
					duration: 0.6,
				},
				'-=1.5'
			)
			.fromTo(
				'.header',
				{
          '--logo-scale-ratio': () => getLogoScaleRatio(),
        },
        {
          '--logo-scale-ratio': 1,
          duration: 1.5
				},
				'-=1.5'
			)
			.fromTo(
				'.header',
				{
					'--logo-top-1': '50vh',
					'--logo-top-2': '0px',
        },
        {
					'--logo-top-1': '0vh',
					'--logo-top-2': () => `${(header?.clientHeight || 0) / 2}px`,
          duration: 1
				},
				'-=1'
			)
			.fromTo(
				'section.products',
				{
					opacity: 0,
					'--blur-strength': 32,
				},
				{
					opacity: 1,
					'--blur-strength': 0,
					duration: 0.8,
				},
				'-=0.8'
			)

		productsTl = gsap
			.timeline({
				scrollTrigger: {
					trigger: '.products__bg',
					start: 'top 30%',
					end: 'bottom bottom',
					scrub: true,
				}
			})
			.to(
				'.products__bg__sheet__img',
				{
					x: '15%'
				}
			)
	}

	const mm = gsap.matchMedia()
	mm.add('(max-width: 767px)', useTimeline)
	mm.add('(min-width: 768px) and (max-width: 1023px)', useTimeline)
	mm.add('(min-width: 1024px)', useTimeline)
}

const useMinorGuitarSwiper = () => {
	const swiperName = '.minor-guitar__swiper'
	const swiperEl = document.querySelector(swiperName)
	if (!swiperEl) return

	const swiperSlides = [...swiperEl.querySelectorAll('.swiper-slide')]
	const swiperControls = document.querySelector('.minor-guitar__swiper__controls')
	const swiperPrevBtn = document.querySelector('.minor-guitar__swiper--prev')
	const swiperNextBtn = document.querySelector('.minor-guitar__swiper--next')
	const swiperFraction = document.querySelector('.minor-guitar__swiper__fraction')

	const setFraction = () => {
		swiperFraction.querySelector('.current').textContent = $activeIndex.get() + 1
		swiperFraction.querySelector('.total').textContent = swiperSlides.length
	}

	// store
	const $activeIndex = atom(0)

	// swiper
	const swiper = new Swiper(swiperEl, {
		speed: 600,
		slidesPerView: 'auto',
		spaceBetween: 64,
		centeredSlides: true,
		centeredSlidesBounds: true,
		breakpoints: {
			1440: {
				spaceBetween: 100
			}
		},
		on: {
			afterInit: () => {
				if (!swiperSlides.length) {
					swiperControls.classList.add('hidden')
				}
				setTimeout(() => {
					$activeIndex.set(0)
				}, 200)
			},
			activeIndexChange: (swiper) => {
				$activeIndex.set(swiper.activeIndex)
			}
		}
	})

	// active index
	$activeIndex.subscribe((index) => {
		setFraction()
		
		if (swiper) {
			swiper.slideTo(index)
			swiperSlides.forEach((slide, i) => {
				slide.classList.toggle('is-active', i === index)
			})
		}

		swiperPrevBtn.classList.toggle('swiper-button-disabled', index === 0)
		swiperNextBtn.classList.toggle('swiper-button-disabled', index === swiperSlides.length - 1)
	})

	// prev btn
	swiperPrevBtn.addEventListener('click', () => {
		$activeIndex.set(
			Math.max($activeIndex.get() - 1, 0)
		)
	})

	// next btn
	swiperNextBtn.addEventListener('click', () => {
		$activeIndex.set(
			Math.min($activeIndex.get() + 1, swiperSlides.length - 1)
		)
	})
}

const useCustomizeGuitarTimer = () => {
	const guitars = [...document.querySelectorAll('.customize__guitar')]
	if (!guitars.length) return

	const $activeIndex = atom(0)
	let intervalId = null

	const setActiveGuitar = (index) => {
		guitars.forEach((guitar, i) => {
			guitar.classList.toggle('is-active', i === index)
		})
	}

	const startTimer = () => {
		intervalId = setInterval(() => {
			$activeIndex.set(
				($activeIndex.get() + 1) % guitars.length
			)
		}, 3000)
	}

	const stopTimer = (index) => {
		$activeIndex.set(index)
		if (intervalId) {
			clearInterval(intervalId)
			intervalId = null
		}
	}

	$activeIndex.subscribe((index) => {
		setActiveGuitar(index)
	})

	startTimer()

	guitars.forEach((guitar, index) => {
		guitar.addEventListener('mouseenter', () => stopTimer(index))
		guitar.addEventListener('mouseleave', () => startTimer())
	})
}

const useNewsSwiper = () => {
	const swiperEl = document.querySelector('.news__swiper')
	if (!swiperEl) return

	const swiperSlides = [...swiperEl.querySelectorAll('.swiper-slide')]
	const swiperControls = document.querySelector('.news__swiper__controls')
	const swiperPrevBtn = document.querySelector('.news__swiper--prev')
	const swiperNextBtn = document.querySelector('.news__swiper--next')
	const swiperFraction = document.querySelector('.news__swiper__fraction')

	const setFraction = () => {
		swiperFraction.querySelector('.current').textContent = $activeIndex.get() + 1
		swiperFraction.querySelector('.total').textContent = swiperSlides.length
	}

	const setIsLoop = (swiper) => {
		const { params } = swiper
		const isLoop = swiperSlides.length > params.slidesPerView
		swiperEl.classList.toggle('swiper-no-swiping', !isLoop)
		swiperControls.classList.toggle('hidden', !isLoop)
	}

	// store
	const $activeIndex = atom(0)

	// swiper
	const swiper = new Swiper(swiperEl, {
		speed: 600,
		loop: true,
		slidesPerView: 1,
		centeredSlides: true,
		navigation: {
			prevEl: swiperPrevBtn,
			nextEl: swiperNextBtn,
		},
		breakpoints: {
			320: {
				slidesPerView: 1,
				spaceBetween: 80,
			},
			480: {
				slidesPerView: 1.6,
				spaceBetween: 80,
			},
			768: {
				slidesPerView: 2,
				spaceBetween: 80,
			},
			1024: {
				slidesPerView: 3,
				spaceBetween: 80,
			},
			1440: {
				slidesPerView: 3,
				spaceBetween: 140,
			},
			1600: {
				slidesPerView: 3,
				spaceBetween: 180,
			},
		},
		on: {
			init(swiper) {
				setIsLoop(swiper)
			},
			breakpoint(swiper) {
				setIsLoop(swiper)
			},
			activeIndexChange(swiper) {
				$activeIndex.set(swiper.realIndex)
			}
		}
	})

	// active index
	$activeIndex.subscribe((index) => {
		setFraction()

		if (swiper) {
			// swiper.slideTo(index)
			// swiperSlides.forEach((slide, i) => {
			// 	slide.classList.toggle('is-active', i === index)
			// })
		}
	})
}


document.addEventListener('DOMContentLoaded', () => {
	useScrollPagination()
	useGsapTimeline()
	useMinorGuitarSwiper()
	useCustomizeGuitarTimer()
	useNewsSwiper()
	useMouseParallax()
	useScrollParallax()
})
